import { useState, useEffect, useMemo } from 'react';
import { Filter, Calendar, Users, User, X } from 'lucide-react';
import { Button } from '@/components/ui/button';
import { Badge } from '@/components/ui/badge';
import {
    Select,
    SelectContent,
    SelectItem,
    SelectTrigger,
    SelectValue,
} from '@/components/ui/select';
import {
    Popover,
    PopoverContent,
    PopoverTrigger,
} from '@/components/ui/popover';
import { Calendar as CalendarComponent } from '@/components/ui/calendar';
import { format, startOfDay, endOfDay, startOfWeek, endOfWeek, startOfMonth, endOfMonth, startOfYear, endOfYear, subDays } from 'date-fns';
import { fr } from 'date-fns/locale';

// Options de période prédéfinies
const PERIOD_PRESETS = [
    { value: 'all', label: 'Toutes périodes', icon: '∞' },
    { value: 'today', label: "Aujourd'hui", icon: '📅' },
    { value: 'yesterday', label: 'Hier', icon: '⏪' },
    { value: 'week', label: 'Cette semaine', icon: '📆' },
    { value: 'month', label: 'Ce mois', icon: '🗓️' },
    { value: 'quarter', label: 'Ce trimestre', icon: '📊' },
    { value: 'year', label: 'Cette année', icon: '🎯' },
    { value: 'custom', label: 'Période personnalisée', icon: '🔧' },
];

export function DashboardFilters({ 
    teams = [], 
    users = [], 
    teamMembers = [],  // Membres de l'équipe du VP
    onFiltersChange,
    currentUserRole,
    currentUserId,
    initialFilters = {}
}) {
    // États des filtres - DÉFAUT: "Ce mois"
    const [periodType, setPeriodType] = useState(initialFilters.periodType || 'month');
    const [customDateRange, setCustomDateRange] = useState({
        from: initialFilters.dateFrom || null,
        to: initialFilters.dateTo || null
    });
    const [scopeType, setScopeType] = useState(initialFilters.scopeType || 'all');
    const [selectedTeamId, setSelectedTeamId] = useState(initialFilters.teamId || null);
    const [selectedUserId, setSelectedUserId] = useState(initialFilters.userId || null);
    
    const [showCalendar, setShowCalendar] = useState(false);
    
    // Déterminer les permissions selon le rôle
    const isAdmin = ['admin_full', 'admin'].includes(currentUserRole);
    const isVP = currentUserRole === 'vp';
    const isApporteur = currentUserRole === 'apporteur';
    
    // Options de scope selon le rôle
    const scopeOptions = useMemo(() => {
        if (isAdmin) {
            // Admin voit tout : équipes + tous les apporteurs
            return [
                { value: 'all', label: 'Tout Valnexo', icon: '🏢' },
                { value: 'team', label: 'Par équipe', icon: '👥' },
                { value: 'user', label: 'Par utilisateur', icon: '👤' },
            ];
        } else if (isVP) {
            // VP voit : tous ses dossiers + filtrer par apporteur de son équipe
            return [
                { value: 'all', label: 'Toute mon équipe', icon: '👥' },
                { value: 'user', label: 'Par apporteur', icon: '👤' },
            ];
        }
        // Apporteur : pas de filtre scope (voit seulement ses dossiers)
        return [];
    }, [isAdmin, isVP]);
    
    // Liste des utilisateurs filtrables selon le rôle
    const filterableUsers = useMemo(() => {
        if (isAdmin) {
            // Admin peut filtrer tous les utilisateurs commerciaux
            return users.filter(u => ['vp', 'apporteur'].includes(u.role));
        } else if (isVP) {
            // VP peut filtrer seulement les membres de son équipe
            return teamMembers.filter(u => u.role === 'apporteur');
        }
        return [];
    }, [isAdmin, isVP, users, teamMembers]);
    
    // Calculer les dates selon la période sélectionnée
    const getDateRange = () => {
        const now = new Date();
        
        switch (periodType) {
            case 'today':
                return { from: startOfDay(now), to: endOfDay(now) };
            case 'yesterday':
                const yesterday = subDays(now, 1);
                return { from: startOfDay(yesterday), to: endOfDay(yesterday) };
            case 'week':
                return { from: startOfWeek(now, { locale: fr }), to: endOfWeek(now, { locale: fr }) };
            case 'month':
                return { from: startOfMonth(now), to: endOfMonth(now) };
            case 'quarter':
                const quarterStart = new Date(now.getFullYear(), Math.floor(now.getMonth() / 3) * 3, 1);
                const quarterEnd = new Date(now.getFullYear(), Math.floor(now.getMonth() / 3) * 3 + 3, 0);
                return { from: quarterStart, to: quarterEnd };
            case 'year':
                return { from: startOfYear(now), to: endOfYear(now) };
            case 'custom':
                return customDateRange;
            case 'all':
            default:
                return { from: null, to: null };
        }
    };
    
    // Notifier le parent quand les filtres changent
    useEffect(() => {
        const dateRange = getDateRange();
        
        const filters = {
            periodType,
            dateFrom: dateRange.from,
            dateTo: dateRange.to,
            scopeType,
            teamId: scopeType === 'team' ? selectedTeamId : null,
            userId: scopeType === 'user' ? selectedUserId : null,
        };
        
        onFiltersChange?.(filters);
    }, [periodType, customDateRange, scopeType, selectedTeamId, selectedUserId]);
    
    // Réinitialiser les filtres
    const resetFilters = () => {
        setPeriodType('all');
        setCustomDateRange({ from: null, to: null });
        setScopeType('all');
        setSelectedTeamId(null);
        setSelectedUserId(null);
    };
    
    // Compter les filtres actifs
    const activeFiltersCount = [
        periodType !== 'all',
        scopeType !== 'all'
    ].filter(Boolean).length;
    
    // Obtenir le label de la période
    const getPeriodLabel = () => {
        if (periodType === 'custom' && customDateRange.from && customDateRange.to) {
            return `${format(customDateRange.from, 'dd/MM/yy')} → ${format(customDateRange.to, 'dd/MM/yy')}`;
        }
        return PERIOD_PRESETS.find(p => p.value === periodType)?.label || 'Toutes périodes';
    };
    
    // Obtenir le label du scope
    const getScopeLabel = () => {
        if (scopeType === 'team' && selectedTeamId) {
            const team = teams.find(t => t.id === selectedTeamId);
            return team?.name || 'Équipe';
        }
        if (scopeType === 'user' && selectedUserId) {
            const user = filterableUsers.find(u => u.id === selectedUserId);
            return user ? `${user.first_name} ${user.last_name}` : 'Utilisateur';
        }
        return scopeOptions.find(s => s.value === scopeType)?.label || 'Tout';
    };
    
    // Peut-on filtrer par scope ? (Admin et VP seulement)
    const canFilterByScope = isAdmin || isVP;
    
    return (
        <div className="flex flex-wrap items-center gap-3 p-4 bg-white rounded-xl border border-slate-200 shadow-sm">
            {/* Icône Filtres */}
            <div className="flex items-center gap-2 text-slate-600">
                <Filter className="w-5 h-5" />
                <span className="font-medium text-sm hidden sm:inline">Filtres</span>
                {activeFiltersCount > 0 && (
                    <Badge variant="secondary" className="bg-purple-100 text-purple-700 text-xs">
                        {activeFiltersCount}
                    </Badge>
                )}
            </div>
            
            <div className="h-6 w-px bg-slate-200" />
            
            {/* Filtre Période - Disponible pour TOUS */}
            <div className="flex items-center gap-2">
                <Calendar className="w-4 h-4 text-slate-400" />
                <Select value={periodType} onValueChange={(value) => {
                    setPeriodType(value);
                    if (value === 'custom') {
                        setShowCalendar(true);
                    }
                }}>
                    <SelectTrigger className="w-auto min-w-[160px] border-slate-200 bg-slate-50 hover:bg-slate-100" data-testid="filter-period">
                        <SelectValue placeholder="Période">{getPeriodLabel()}</SelectValue>
                    </SelectTrigger>
                    <SelectContent>
                        {PERIOD_PRESETS.map(opt => (
                            <SelectItem key={opt.value} value={opt.value}>
                                <span className="flex items-center gap-2">
                                    <span>{opt.icon}</span>
                                    <span>{opt.label}</span>
                                </span>
                            </SelectItem>
                        ))}
                    </SelectContent>
                </Select>
                
                {/* Calendrier pour période personnalisée */}
                {periodType === 'custom' && (
                    <Popover open={showCalendar} onOpenChange={setShowCalendar}>
                        <PopoverTrigger asChild>
                            <Button 
                                variant="outline" 
                                size="sm"
                                className="border-purple-200 text-purple-700 hover:bg-purple-50"
                            >
                                {customDateRange.from && customDateRange.to 
                                    ? `${format(customDateRange.from, 'dd/MM')} - ${format(customDateRange.to, 'dd/MM')}`
                                    : 'Choisir dates'
                                }
                            </Button>
                        </PopoverTrigger>
                        <PopoverContent className="w-auto p-0" align="start">
                            <CalendarComponent
                                mode="range"
                                selected={customDateRange}
                                onSelect={(range) => {
                                    setCustomDateRange(range || { from: null, to: null });
                                    if (range?.from && range?.to) {
                                        setShowCalendar(false);
                                    }
                                }}
                                locale={fr}
                                numberOfMonths={2}
                                className="rounded-md border"
                            />
                        </PopoverContent>
                    </Popover>
                )}
            </div>
            
            {/* Filtre Scope - Admin et VP seulement */}
            {canFilterByScope && scopeOptions.length > 0 && (
                <>
                    <div className="h-6 w-px bg-slate-200" />
                    
                    <div className="flex items-center gap-2">
                        <Users className="w-4 h-4 text-slate-400" />
                        <Select value={scopeType} onValueChange={(value) => {
                            setScopeType(value);
                            // Reset les sélections quand on change de type
                            setSelectedTeamId(null);
                            setSelectedUserId(null);
                        }}>
                            <SelectTrigger className="w-auto min-w-[140px] border-slate-200 bg-slate-50 hover:bg-slate-100" data-testid="filter-scope">
                                <SelectValue placeholder="Scope">{getScopeLabel()}</SelectValue>
                            </SelectTrigger>
                            <SelectContent>
                                {scopeOptions.map(opt => (
                                    <SelectItem key={opt.value} value={opt.value}>
                                        <span className="flex items-center gap-2">
                                            <span>{opt.icon}</span>
                                            <span>{opt.label}</span>
                                        </span>
                                    </SelectItem>
                                ))}
                            </SelectContent>
                        </Select>
                        
                        {/* Sélecteur d'équipe - Admin seulement */}
                        {scopeType === 'team' && isAdmin && teams.length > 0 && (
                            <Select value={selectedTeamId || ''} onValueChange={setSelectedTeamId}>
                                <SelectTrigger className="w-auto min-w-[150px] border-blue-200 bg-blue-50 hover:bg-blue-100" data-testid="filter-team">
                                    <SelectValue placeholder="Choisir équipe" />
                                </SelectTrigger>
                                <SelectContent>
                                    {teams.map(team => (
                                        <SelectItem key={team.id} value={team.id}>
                                            <span className="flex items-center gap-2">
                                                <span>👥</span>
                                                <span>{team.name}</span>
                                                {team.vp_name && (
                                                    <Badge variant="outline" className="text-xs ml-1">
                                                        VP: {team.vp_name}
                                                    </Badge>
                                                )}
                                            </span>
                                        </SelectItem>
                                    ))}
                                </SelectContent>
                            </Select>
                        )}
                        
                        {/* Sélecteur d'utilisateur - Admin ou VP */}
                        {scopeType === 'user' && filterableUsers.length > 0 && (
                            <Select value={selectedUserId || ''} onValueChange={setSelectedUserId}>
                                <SelectTrigger className="w-auto min-w-[180px] border-green-200 bg-green-50 hover:bg-green-100" data-testid="filter-user">
                                    <SelectValue placeholder={isVP ? "Choisir apporteur" : "Choisir utilisateur"} />
                                </SelectTrigger>
                                <SelectContent>
                                    {filterableUsers.map(user => (
                                        <SelectItem key={user.id} value={user.id}>
                                            <span className="flex items-center gap-2">
                                                <Badge 
                                                    variant="outline" 
                                                    className={user.role === 'vp' ? 'bg-purple-50 text-purple-700 border-purple-200' : 'bg-blue-50 text-blue-700 border-blue-200'}
                                                >
                                                    {user.role === 'vp' ? 'VP' : 'App'}
                                                </Badge>
                                                <span>{user.first_name} {user.last_name}</span>
                                            </span>
                                        </SelectItem>
                                    ))}
                                </SelectContent>
                            </Select>
                        )}
                        
                        {/* Message si pas d'apporteurs dans l'équipe */}
                        {scopeType === 'user' && isVP && filterableUsers.length === 0 && (
                            <span className="text-sm text-slate-500 italic">
                                Aucun apporteur dans votre équipe
                            </span>
                        )}
                    </div>
                </>
            )}
            
            {/* Indicateur pour Apporteur */}
            {isApporteur && (
                <>
                    <div className="h-6 w-px bg-slate-200" />
                    <div className="flex items-center gap-2 text-slate-500">
                        <User className="w-4 h-4" />
                        <span className="text-sm">Mes dossiers</span>
                    </div>
                </>
            )}
            
            {/* Bouton Reset */}
            {activeFiltersCount > 0 && (
                <>
                    <div className="h-6 w-px bg-slate-200" />
                    <Button 
                        variant="ghost" 
                        size="sm" 
                        onClick={resetFilters}
                        className="text-slate-500 hover:text-slate-700 hover:bg-slate-100"
                        data-testid="reset-filters"
                    >
                        <X className="w-4 h-4 mr-1" />
                        Réinitialiser
                    </Button>
                </>
            )}
        </div>
    );
}

export default DashboardFilters;
